export interface TextOverlayOptions {
  text: string;
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: string;
  textColor?: string;
  backgroundColor?: string;
  backgroundOpacity?: number;
  padding?: number;
  maxWidth?: number;
}

export interface UserSettings {
  text_color: string;
  background_color: string;
  background_opacity: number;
  font_size: number;
  text_position_y: number;
}

export async function createImageWithTextOverlay(
  imageUrl: string, 
  options: TextOverlayOptions,
  userSettings?: UserSettings
): Promise<string> {
  console.log('🖼️ Starting text overlay for:', imageUrl);
  console.log('📝 Text to overlay:', options.text);
  
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      console.log('✅ Image loaded successfully:', img.width, 'x', img.height);
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          console.error('❌ Unable to get canvas context');
          reject(new Error('Unable to get canvas context'));
          return;
        }

        // Standardize canvas dimensions (Instagram story format)
        const STANDARD_WIDTH = 1080;
        const STANDARD_HEIGHT = 1920;
        canvas.width = STANDARD_WIDTH;
        canvas.height = STANDARD_HEIGHT;
        console.log('🎨 Canvas created with standard dimensions:', canvas.width, 'x', canvas.height);

        // Draw the original image to fit the standardized canvas
        drawImageToFit(ctx, img, canvas.width, canvas.height);
        console.log('🖼️ Image fitted to standard canvas size');

        // Apply text overlay with standardized parameters
        const overlayDataUrl = applyTextOverlay(ctx, canvas, options, userSettings);
        console.log('✅ Text overlay applied successfully');
        resolve(overlayDataUrl);
      } catch (error) {
        console.error('❌ Error in text overlay:', error);
        reject(error);
      }
    };

    img.onerror = (error) => {
      console.error('❌ Failed to load image:', imageUrl, error);
      reject(new Error(`Failed to load image: ${imageUrl}`));
    };

    console.log('🔄 Loading image...');
    img.src = imageUrl;
  });
}

function drawImageToFit(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  canvasWidth: number,
  canvasHeight: number
): void {
  // Calculate scaling to cover the entire canvas while maintaining aspect ratio
  const scaleX = canvasWidth / img.width;
  const scaleY = canvasHeight / img.height;
  const scale = Math.max(scaleX, scaleY); // Use max to cover entire canvas

  // Calculate new dimensions
  const newWidth = img.width * scale;
  const newHeight = img.height * scale;

  // Calculate position to center the image
  const x = (canvasWidth - newWidth) / 2;
  const y = (canvasHeight - newHeight) / 2;

  // Fill background with a neutral color in case image doesn't cover entire canvas
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  // Draw the scaled and centered image
  ctx.drawImage(img, x, y, newWidth, newHeight);
}

function applyTextOverlay(
  ctx: CanvasRenderingContext2D, 
  canvas: HTMLCanvasElement, 
  options: TextOverlayOptions,
  userSettings?: UserSettings
): string {
  const {
    text,
    fontSize = 64,
    fontFamily = 'Arial',
    fontWeight = 'bold',
    textColor = 'white',
    backgroundColor = 'rgba(0, 0, 0, 0.6)',
    padding = 30
  } = options;

  console.log('🎨 Starting text overlay for canvas:', `${canvas.width}x${canvas.height}`);
  console.log('📝 Text to wrap:', text);

  // Set font FIRST before any text measurements
  const fontString = `${fontWeight} ${fontSize}px ${fontFamily}`;
  ctx.font = fontString;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  console.log('🔤 Font set to:', fontString);

  // Calculate conservative maxWidth for text wrapping
  // Use 70% of canvas width to ensure proper wrapping
  const availableWidth = canvas.width * 0.7;
  console.log('📏 Available width for text:', availableWidth);

  // Wrap text with robust width calculation
  const wrappedLines = wrapTextRobust(ctx, text, availableWidth, fontSize);
  console.log('📝 Text wrapped into', wrappedLines.length, 'lines:', wrappedLines);
  
  if (wrappedLines.length === 0) {
    console.log('⚠️ No text lines to render');
    return canvas.toDataURL();
  }

  // Calculate text block dimensions
  const lineHeight = fontSize * 1.4; // Good line spacing
  const textBlockHeight = wrappedLines.length * lineHeight;
  
  // Measure actual width of each line after wrapping
  const lineWidths = wrappedLines.map(line => ctx.measureText(line).width);
  const textBlockWidth = Math.max(...lineWidths);
  
  console.log('📏 Text measurements:', {
    lineWidths,
    textBlockWidth,
    lineHeight,
    textBlockHeight
  });

  // Calculate background dimensions with padding
  const bgWidth = Math.min(textBlockWidth + (padding * 2), canvas.width * 0.9);
  const bgHeight = textBlockHeight + (padding * 2);
  const bgX = (canvas.width - bgWidth) / 2;
  
  // Position background based on user setting or default
  const minY = 20;
  const maxY = canvas.height - bgHeight - 20;
  const preferredY = userSettings?.text_position_y ? 
    canvas.height * userSettings.text_position_y : 
    canvas.height * 0.65; // Default position
  const bgY = Math.max(minY, Math.min(preferredY, maxY));

  console.log('📦 Background box:', { bgX, bgY, bgWidth, bgHeight });

  // Draw background rectangle
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(bgX, bgY, bgWidth, bgHeight);

  // Draw text lines
  ctx.fillStyle = textColor;
  const textStartY = bgY + padding;
  const textCenterX = canvas.width / 2;

  wrappedLines.forEach((line, index) => {
    const y = textStartY + (index * lineHeight);
    ctx.fillText(line, textCenterX, y);
    console.log(`📝 Line ${index + 1}: "${line}" at (${textCenterX}, ${y})`);
  });

  console.log('✅ Text overlay complete');
  return canvas.toDataURL();
}

function wrapTextRobust(
  ctx: CanvasRenderingContext2D, 
  text: string, 
  maxWidth: number, 
  fontSize: number
): string[] {
  console.log('🔄 Starting robust text wrapping...');
  console.log('📏 Max width:', maxWidth, 'Font size:', fontSize);
  
  // Verify font is set by measuring a test character
  const testWidth = ctx.measureText('M').width;
  console.log('🔤 Test character "M" width:', testWidth);
  
  if (testWidth === 0) {
    console.error('❌ Font not set properly - measureText returning 0');
    return [text]; // Fallback to single line
  }

  // Split text into words, handling multiple spaces
  const words = text.trim().split(/\s+/).filter(word => word.length > 0);
  console.log('📝 Words to wrap:', words);
  
  if (words.length === 0) {
    return [];
  }

  const lines: string[] = [];
  let currentLine = '';

  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    
    // Handle very long words that exceed maxWidth on their own
    const wordWidth = ctx.measureText(word).width;
    if (wordWidth > maxWidth) {
      console.log('⚠️ Word too long:', word, 'width:', wordWidth);
      
      // If current line has content, finish it first
      if (currentLine.trim()) {
        lines.push(currentLine.trim());
        currentLine = '';
      }
      
      // Break the long word or add it as-is (we'll handle overflow in display)
      lines.push(word);
      continue;
    }

    // Test adding this word to current line
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const testWidth = ctx.measureText(testLine).width;
    
    console.log(`🔍 Testing line: "${testLine}" width: ${testWidth} vs max: ${maxWidth}`);

    if (testWidth <= maxWidth) {
      // Word fits, add it to current line
      currentLine = testLine;
    } else {
      // Word doesn't fit, finish current line and start new one
      if (currentLine.trim()) {
        lines.push(currentLine.trim());
        console.log('✅ Completed line:', currentLine.trim());
      }
      currentLine = word;
    }
  }

  // Add the last line if it has content
  if (currentLine.trim()) {
    lines.push(currentLine.trim());
    console.log('✅ Final line:', currentLine.trim());
  }

  console.log('📝 Wrapped into', lines.length, 'lines:', lines);

  // Limit to maximum 5 lines for readability
  if (lines.length > 5) {
    console.log('⚠️ Too many lines, truncating to 5');
    const truncatedLines = lines.slice(0, 5);
    // Add ellipsis to last line if we truncated
    if (truncatedLines[4]) {
      truncatedLines[4] = truncatedLines[4] + '...';
    }
    return truncatedLines;
  }

  return lines;
}

// Keep old function for backward compatibility
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  return wrapTextRobust(ctx, text, maxWidth, 64);
}

// Helper function that preserves original image dimensions
export async function createImageWithTextPreview(
  imageUrl: string, 
  text: string,
  userSettings?: UserSettings
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Unable to get canvas context'));
          return;
        }

        // Use original image dimensions
        canvas.width = img.width;
        canvas.height = img.height;

        // Draw the original image
        ctx.drawImage(img, 0, 0);

        // Apply text overlay with user settings or dimensions relative to image size
        const fontSize = userSettings?.font_size || Math.max(24, Math.min(64, img.width / 20)); // Use user setting or dynamic
        const padding = Math.max(15, Math.min(30, img.width / 40)); // Dynamic padding
        const textColor = userSettings?.text_color || 'white';
        const backgroundColor = userSettings ? 
          hexToRgba(userSettings.background_color, userSettings.background_opacity) : 
          'rgba(0, 0, 0, 0.6)';
        
        const overlayDataUrl = applyTextOverlayPreserveSize(ctx, canvas, {
          text,
          fontSize,
          fontFamily: 'Arial',
          fontWeight: 'bold',
          textColor,
          backgroundColor,
          padding
        }, userSettings);
        
        // Compress the result to reduce payload size
        compressImagePreview(overlayDataUrl, img.width, img.height).then(resolve).catch(reject);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = (error) => {
      reject(new Error(`Failed to load image: ${imageUrl}`));
    };

    img.src = imageUrl;
  });
}

async function compressImagePreview(dataUrl: string, originalWidth: number, originalHeight: number): Promise<string> {
  try {
    // Import compression utility dynamically to avoid circular deps
    const { compressImage } = await import('./imageCompression');
    
    // Use reasonable compression settings for preview
    return compressImage(dataUrl, {
      quality: 0.85,
      maxWidth: Math.min(originalWidth, 800), // Cap at 800px width for previews
      maxHeight: Math.min(originalHeight, 800), // Cap at 800px height for previews
      outputFormat: 'jpeg'
    });
  } catch (error) {
    console.error('Error compressing preview image:', error);
    // Return original if compression fails
    return dataUrl;
  }
}

function applyTextOverlayPreserveSize(
  ctx: CanvasRenderingContext2D, 
  canvas: HTMLCanvasElement, 
  options: TextOverlayOptions,
  userSettings?: UserSettings
): string {
  const {
    text,
    fontSize = 32,
    fontFamily = 'Arial',
    fontWeight = 'bold',
    textColor = 'white',
    backgroundColor = 'rgba(0, 0, 0, 0.6)',
    padding = 20
  } = options;

  // Set font
  const fontString = `${fontWeight} ${fontSize}px ${fontFamily}`;
  ctx.font = fontString;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';

  // Calculate available width (70% of canvas width)
  const availableWidth = canvas.width * 0.7;

  // Wrap text
  const wrappedLines = wrapTextRobust(ctx, text, availableWidth, fontSize);
  
  if (wrappedLines.length === 0) {
    return canvas.toDataURL();
  }

  // Calculate text block dimensions
  const lineHeight = fontSize * 1.4;
  const textBlockHeight = wrappedLines.length * lineHeight;
  
  // Measure actual width of each line
  const lineWidths = wrappedLines.map(line => ctx.measureText(line).width);
  const textBlockWidth = Math.max(...lineWidths);
  
  // Calculate background dimensions with padding
  const bgWidth = Math.min(textBlockWidth + (padding * 2), canvas.width * 0.9);
  const bgHeight = textBlockHeight + (padding * 2);
  const bgX = (canvas.width - bgWidth) / 2;
  
  // Position background based on user setting or default
  const minY = 20;
  const maxY = canvas.height - bgHeight - 20;
  const preferredY = userSettings?.text_position_y ? 
    canvas.height * userSettings.text_position_y : 
    canvas.height * 0.75; // Default position
  const bgY = Math.max(minY, Math.min(preferredY, maxY));

  // Draw background rectangle
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(bgX, bgY, bgWidth, bgHeight);

  // Draw text lines
  ctx.fillStyle = textColor;
  const textStartY = bgY + padding;
  const textCenterX = canvas.width / 2;

  wrappedLines.forEach((line, index) => {
    const y = textStartY + (index * lineHeight);
    ctx.fillText(line, textCenterX, y);
  });

  return canvas.toDataURL();
}

// Helper function with custom user settings for standardized dimensions
export async function createStoryImageWithText(
  imageUrl: string, 
  text: string,
  userSettings?: UserSettings
): Promise<string> {
  // Use user settings or defaults
  const fontSize = userSettings?.font_size || 64;
  const textColor = userSettings?.text_color || 'white';
  const backgroundColor = userSettings?.background_color || '#000000';
  const backgroundOpacity = userSettings?.background_opacity || 0.6;
  
  // Convert hex background color to rgba with opacity
  const bgColorRgba = hexToRgba(backgroundColor, backgroundOpacity);
  
  const overlayResult = await createImageWithTextOverlay(imageUrl, {
    text,
    fontSize,
    fontFamily: 'Arial',
    fontWeight: 'bold',
    textColor,
    backgroundColor: bgColorRgba,
    padding: 30, // Increased padding for better proportions
    maxWidth: 918 // 85% of standardized 1080px width
  }, userSettings);

  // Import compression utility dynamically to avoid circular deps
  const { compressImage } = await import('./imageCompression');
  
  // Compress the result to reduce payload size
  // Use higher max dimensions since we're starting with standardized 1080x1920
  return compressImage(overlayResult, {
    quality: 0.85,
    maxWidth: 1080,
    maxHeight: 1920,
    outputFormat: 'jpeg'
  });
}

// Helper function to convert hex color to rgba
function hexToRgba(hex: string, alpha: number): string {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse r, g, b values
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}